import React, { useEffect, useState } from 'react';
import './CustomerReview.css';
import { getImageUrl } from '../../utils/imageUtils';

const CustomerReview = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [isManual, setIsManual] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const baseURL="https://backendweb.properteck.lk";

  const getInitials = (fullName = '') => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Fetch approved reviews
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${baseURL}/api/reviews?approved=true`);
        const data = await res.json();
        const list = Array.isArray(data?.reviews) ? data.reviews : Array.isArray(data) ? data : [];
        setReviews(list);
      } catch (err) {
        setError('Failed to load reviews');
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    loadReviews();
  }, []);

  // Responsive items per slide (match other home components)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setItemsPerSlide(3);
      } else if (window.innerWidth >= 768) {
        setItemsPerSlide(2);
      } else {
        setItemsPerSlide(1);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate total slides without wrapping - only show complete slides or partial last slide
  const totalSlides = Math.max(1, Math.ceil(reviews.length / itemsPerSlide));

  // Auto-rotate only if we have enough reviews to warrant multiple slides
  useEffect(() => {
    if (isManual || reviews.length <= itemsPerSlide) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = prev + 1;
        // Reset to 0 when we've shown all reviews
        if (nextIndex >= totalSlides) {
          return 0;
        }
        return nextIndex;
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isManual, totalSlides, reviews.length, itemsPerSlide]);

  const getCurrentSlideItems = () => {
    if (reviews.length === 0) return [];
    
    const startIndex = currentIndex * itemsPerSlide;
    const endIndex = Math.min(startIndex + itemsPerSlide, reviews.length);
    
    // Only return the available reviews, don't wrap around to avoid duplicates
    return reviews.slice(startIndex, endIndex);
  };

  const handleIndicatorClick = (index) => {
    setCurrentIndex(index);
    setIsManual(true);
    setTimeout(() => setIsManual(false), 6000);
  };

  const prev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
    setIsManual(true);
    setTimeout(() => setIsManual(false), 6000);
  };

  const next = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    setIsManual(true);
    setTimeout(() => setIsManual(false), 6000);
  };

  const currentSlideItems = getCurrentSlideItems();

  return (
    <div className="review-carousel-container">
      <div className="review-header">
        <h2 className="review-title">What Our Customers Say</h2>
        <div className="review-underline" />
      </div>

      {error && (
        <div style={{ color: '#dc3545', marginBottom: 8, fontSize: 14 }}>{error}</div>
      )}

      {loading && (
        <div style={{ color: '#e0e0e0', marginBottom: 8, fontSize: 14 }}>Loading...</div>
      )}

      {(!loading && reviews.length === 0) && (
        <div style={{ color: '#9aa0a6', marginBottom: 8, fontSize: 14 }}>No reviews yet.</div>
      )}

      <div className="review-carousel">
        {/* Only show navigation buttons if we have multiple slides */}
        {totalSlides > 1 && (
          <button className="review-nav left" onClick={prev} aria-label="Previous reviews">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="https://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className={`review-cards-wrapper items-${itemsPerSlide}`}>
          {currentSlideItems.map((item) => (
            <div key={`${item._id || item.id}`} className="review-card">
              <div className="review-stars">
                {Array.from({ length: Math.max(1, Math.min(5, Number(item.rating) || 5)) }).map((_, i) => (
                  <span key={i} className="star">★</span>
                ))}
              </div>

              <blockquote className="review-text">"{item.review}"</blockquote>

              <div className="reviewer">
                <div className="avatar">
                  {item.avatarUrl ? (
      <img
        className="avatar-img"
        src={getImageUrl(item.avatarUrl)}   // ✅ use util here
        alt={item.name}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = '/no-image.svg';
        }}
      />
                  ) : (
                    getInitials(item.name)
                  )}
                </div>
                <div className="meta">
                  <div className="name">{item.name}</div>
                  <div className="role">
                    {[item.role, item.company].filter(Boolean).join(' at ')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Only show navigation buttons if we have multiple slides */}
        {totalSlides > 1 && (
          <button className="review-nav right" onClick={next} aria-label="Next reviews">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="https://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Only show indicators if we have multiple slides */}
      {totalSlides > 1 && (
        <div className="review-indicators">
          {Array.from({ length: totalSlides }).map((_, idx) => (
            <span
              key={idx}
              onClick={() => handleIndicatorClick(idx)}
              className={`review-dot ${idx === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerReview;